import type {
  CabinetGameConfig,
  GameConfiguration,
} from '@sirius/contracts/admin'
import type { TFunction } from 'i18next'
import { formatDateTime } from '~/i18n/format'
import { USE_DEFAULT, fromOwn } from '~/lib/inheritable'
import type { GameNames } from './gameNames'

/** One game of a cabinet on its show page: the configuration it plays. */
export type ConfigurationRow = {
  gameId: string
  /** "…" while the names load, the id for a game without a name */
  name: string
  /**
   * the row's whole sentence: `Book of Ra 2: running Default configuration ·
   * saved <date>` for the game the cabinet plays, else `…: starts on …`
   */
  summary: string
  /**
   * `Low · saved <date> pending until restart` while the cabinet runs
   * another version than its next boot plays
   */
  pending: string | null
  /**
   * the configuration was assigned to this cabinet (by staff or the
   * tenant's owners/admins), not Use default
   */
  assigned: boolean
}

/** A version as people read it: its configuration's name and when it was saved. */
type Version = { name: string; version: number; savedAt: Date }

/**
 * The configurations of a cabinet's games, sorted by name. The cabinet boots
 * a configuration's current version, so a new one (an assigned one, a new
 * default, a save of either) reaches the cabinet on its next boot with no
 * round open, and is pending until then. `formatDate` shows a save's time
 * (the browser's locale by default).
 */
export function configurationRows(
  gameConfigs: readonly CabinetGameConfig[],
  { byId, isLoading }: GameNames,
  t: TFunction,
  formatDate: (date: Date) => string = (date) => formatDateTime(date),
): ConfigurationRow[] {
  const values = ({ name, savedAt }: Version) => ({
    name,
    date: formatDate(savedAt),
  })
  return gameConfigs
    .map((entry) => {
      const assigned = versionOf(
        entry.assignedConfigurationName,
        entry.assignedConfigVersion,
        entry.assignedConfigSavedAt,
      )
      const running = versionOf(
        entry.runningConfigurationName,
        entry.runningConfigVersion,
        entry.runningConfigSavedAt,
      )
      const game = isLoading ? '…' : (byId.get(entry.gameId) ?? entry.gameId)
      return {
        gameId: entry.gameId,
        name: game,
        ...(running === null
          ? {
              summary:
                assigned === null
                  ? t('cabinetsPage.configurations.noConfigRow', { game })
                  : t('cabinetsPage.configurations.startsOnRow', {
                      game,
                      ...values(assigned),
                    }),
              pending: null,
            }
          : {
              summary: t('cabinetsPage.configurations.runningRow', {
                game,
                ...values(running),
              }),
              pending:
                assigned !== null && !sameVersion(assigned, running)
                  ? t(
                      'cabinetsPage.configurations.pendingRow',
                      values(assigned),
                    )
                  : null,
            }),
        assigned: entry.configurationId !== null,
      }
    })
    .toSorted((left, right) => left.name.localeCompare(right.name))
}

function versionOf(
  name: string | null,
  version: number | null,
  savedAt: Date | null,
): Version | null {
  return name === null || version === null || savedAt === null
    ? null
    : { name, version, savedAt }
}

/**
 * Names are unique within a game and a number within its configuration, so
 * two versions are the same when both are.
 */
const sameVersion = (left: Version, right: Version) =>
  left.name === right.name && left.version === right.version

/** The select's value as `configurationId`: null for Use default (`USE_DEFAULT`). */
export function toConfigurationId(value: string | undefined): string | null {
  return value === undefined || value === USE_DEFAULT ? null : value
}

/** The select's value for `configurationId`: `USE_DEFAULT` for null. */
export const fromConfigurationId = fromOwn

/**
 * The `gameConfigs` of a save by staff or the tenant's owners/admins: one
 * entry per game the cabinet offers, from the form's configuration per game
 * (missing: Use default).
 */
export function gameConfigsInput(
  gameIds: readonly string[],
  configurations: Readonly<Record<string, string>>,
) {
  return gameIds.map((gameId) => ({
    gameId,
    configurationId: toConfigurationId(configurations[gameId]),
  }))
}

/**
 * A configuration as a select lists it: its name, "own" for the tenant's
 * own, and when it was last saved (`Low · own · saved <date>`).
 */
export function configurationOptionLabel(
  configuration: Pick<GameConfiguration, 'name' | 'tenantId' | 'updatedAt'>,
  t: TFunction,
  formatDate: (date: Date) => string = (date) => formatDateTime(date),
): string {
  return t(
    configuration.tenantId === null
      ? 'cabinetsPage.configurations.optionLabel'
      : 'cabinetsPage.configurations.optionLabelOwn',
    { name: configuration.name, date: formatDate(configuration.updatedAt) },
  )
}
